import { create } from 'zustand';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Platform settings.
 *
 * These are what make one deployment differ from another (ADR-015): store
 * identity, tax, feature flags, the home page's composition, and the content
 * pages. The API validates shape and refuses unknown keys, so this screen edits
 * what exists rather than inventing keys nothing reads.
 */

export interface SettingEntry {
  key: string;
  value: unknown;
}

export interface SettingsState {
  entries: SettingEntry[];
  loading: boolean;
  saving: string | null;
  error: string | null;
  savedKey: string | null;

  load: () => Promise<void>;
  save: (key: string, value: unknown) => Promise<boolean>;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  entries: [],
  loading: false,
  saving: null,
  error: null,
  savedKey: null,

  async load() {
    set({ loading: true, error: null });

    try {
      const all = await withSession(() => adminApi.settings.all());
      set({
        entries: Object.entries(all).map(([key, value]) => ({ key, value })),
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load settings.') });
    }
  },

  async save(key, value) {
    set({ saving: key, error: null, savedKey: null });

    try {
      const saved = await withSession(() => adminApi.settings.update(key, value));
      // What the API stored, not what was sent: it tidies some values on the
      // way in — a section's new id, a page's "last updated" (ADR-056, ADR-058).
      const stored = saved && typeof saved === 'object' && 'value' in saved ? saved.value : value;

      set((state) => ({
        saving: null,
        savedKey: key,
        entries: state.entries.map((entry) => (entry.key === key ? { key, value: stored } : entry)),
      }));
      return true;
    } catch (error) {
      // The API's shape checks produce the useful message here — a bad tax rate
      // or a malformed layout block is rejected with the reason.
      set({ saving: null, error: errorMessage(error, 'Could not save this setting.') });
      return false;
    }
  },
}));

/** Reads a setting's value by key, or undefined if the deployment lacks it. */
export function settingValue(entries: SettingEntry[], key: string): unknown {
  return entries.find((entry) => entry.key === key)?.value;
}
